/**
 * ===========================================
 * StudySpace ERP Frontend - LoadingSpinner Component
 * ===========================================
 * 
 * Reusable loading spinner with size variants.
 * Uses react-spinners for consistent loading UI.
 * 
 * @file src/components/common/LoadingSpinner.jsx
 * @description Loading spinner component with size variants
 */

import PropTypes from 'prop-types';
import { ClipLoader } from 'react-spinners';

/**
 * Size variants for the spinner (in pixels)
 */
const SIZES = {
  sm: 20,
  md: 35,
  lg: 50,
  xl: 70
};

/**
 * LoadingSpinner Component
 * Displays a centered loading spinner
 * 
 * @param {Object} props - Component props
 * @param {string} [props.size='md'] - Size variant (sm, md, lg, xl)
 * @param {string} [props.color='#3B82F6'] - Spinner color
 * @param {boolean} [props.fullScreen=false] - Whether to center in full screen
 * @param {string} [props.className] - Additional CSS classes
 */
const LoadingSpinner = ({
  size = 'md',
  color = '#3B82F6',
  fullScreen = false,
  className = ''
}) => {
  const spinnerSize = SIZES[size] || SIZES.md;

  const containerClasses = fullScreen
    ? 'fixed inset-0 flex items-center justify-center bg-white bg-opacity-80 z-50'
    : `flex items-center justify-center ${className}`;

  return (
    <div className={containerClasses}>
      <ClipLoader
        color={color}
        size={spinnerSize}
        aria-label="Loading"
      />
    </div>
  );
};

LoadingSpinner.propTypes = {
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  color: PropTypes.string,
  fullScreen: PropTypes.bool,
  className: PropTypes.string
};

export default LoadingSpinner;
